import React, { useState } from 'react';
import { Box, Button, ButtonBase, Popover, Stack } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ReplyIcon from '@mui/icons-material/Reply';

import { buttonSizeSx, secondaryButtonSx } from 'theme/buttons';
import { countBadgeSx } from 'theme/badges';
import { popoverShadow } from 'theme/shadows';
import { pluralize } from 'utils/string';

interface ICandidateFiltersProps {
  activeCount: number;
  canFilter: boolean;
  onClear: () => void;
  children: React.ReactNode;
}

export const CandidateFilters: React.FC<ICandidateFiltersProps> = ({
  activeCount,
  canFilter,
  onClear,
  children,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <ButtonBase
        disableRipple
        disabled={!canFilter && !activeCount}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          height: '2.5rem',
          px: 1.5,
          gap: 0.75,
          flexShrink: 0,
          borderRadius: '4px',
          border: '1px solid',
          fontSize: '0.875rem',
          lineHeight: 1.5,
          whiteSpace: 'nowrap',
          transition: 'background-color 150ms, border-color 150ms, color 150ms',
          ...(activeCount
            ? {
                borderColor: 'text.link',
                backgroundColor: 'text.linkLight',
                color: 'text.linkDark',
                fontWeight: 500,
              }
            : {
                borderColor: 'background.divider',
                backgroundColor: 'common.white',
                color: 'text.primary',
                '&:hover': { backgroundColor: 'highlight.neutral' },
                '&.Mui-disabled, &.Mui-disabled .MuiSvgIcon-root': {
                  color: 'text.lightGrey',
                },
              }),
        }}
      >
        <FilterListIcon sx={{ fontSize: '1.25rem' }} />
        Filters
        {activeCount > 0 && (
          <Box component="span" sx={countBadgeSx}>
            {activeCount}
          </Box>
        )}
        <ExpandMoreRoundedIcon
          sx={{ fontSize: '1.25rem', color: 'text.secondary' }}
        />
      </ButtonBase>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              mt: 0.5,
              width: 'min(32rem, calc(100vw - 2rem))',
              maxHeight: '70vh',
              border: '1px solid',
              borderColor: 'background.divider',
              boxShadow: popoverShadow,
            },
          },
        }}
      >
        <Stack gap={2}>
          {children}

          <Button
            variant="outlined"
            fullWidth
            startIcon={<ReplyIcon />}
            disabled={activeCount === 0}
            onClick={onClear}
            sx={{ ...buttonSizeSx.md, ...secondaryButtonSx }}
          >
            {activeCount === 0
              ? 'No filters applied'
              : `Clear ${pluralize(activeCount, 'filter')}`}
          </Button>
        </Stack>
      </Popover>
    </>
  );
};
